/**
 * Reconciliation — what our books say against what the custodian and the rail say.
 *
 * An open delta is the only thing on this screen that matters. A run that found nothing is
 * worth one line; a run that found a disagreement is a number somebody has to go and explain,
 * so the deltas are listed first and the run history sits underneath as context.
 *
 * The run button resolves when the job is **queued**, not when it finishes, and the page says
 * so rather than spinning. A full reconciliation walks every custodian balance and every
 * unmatched movement — far past the point where an HTTP request should still be open, and past
 * the 120-second statement ceiling on the host. Pretending otherwise would give an operator a
 * spinner that never resolves and no way to tell a slow run from a broken one.
 */
import { useState } from 'react';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { adminCan } from '../contracts/permissions.js';
import type { ReconciliationScope } from '../contracts/types.js';
import { Badge, Button, Card, EmptyState, ErrorBox, LoadingState, Select } from '../components/ui.js';
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';
import { formatDateTime, relativeTime } from '../lib/format.js';

const SCOPES: readonly ReconciliationScope[] = ['full', 'custody', 'psp', 'crosswalk'];

/** What each scope actually walks, so the choice is not four words with no meaning. */
const SCOPE_COPY: Record<ReconciliationScope, string> = {
  full: 'Everything below, in one go.',
  custody: "Your provider's balances against your own records.",
  psp: "Your payment provider's balance against your naira.",
  crosswalk: 'Movements recorded on one side but not the other.',
};

/** Friendly names for the scope choices, so the dropdown is not a list of raw slugs. */
const SCOPE_LABELS: Record<ReconciliationScope, string> = {
  full: 'Everything',
  custody: 'Crypto provider balances',
  psp: 'Payment provider balance',
  crosswalk: 'Movements on one side only',
};

/** Plain words for the run outcomes shown in the history table. */
const RUN_STATUS_LABELS: Record<'running' | 'clean' | 'deltas' | 'failed', string> = {
  running: 'Running',
  clean: 'Everything matched',
  deltas: 'Differences found',
  failed: 'Failed',
};

export function TreasuryReconciliationPage(): JSX.Element {
  const { api, session } = useAuth();
  const toast = useToast();
  const canRun = session !== null && adminCan(session.me.role, 'rates:write');
  const state = useAsync(() => api.treasuryReconciliation(), []);
  const [scope, setScope] = useState<ReconciliationScope>('full');
  const [queueing, setQueueing] = useState(false);

  async function run(): Promise<void> {
    setQueueing(true);
    try {
      await api.runTreasuryReconciliation(scope);
      toast.push(
        'success',
        'Check queued.',
        'It starts within a minute. Reload this page to see the result.',
      );
      state.reload();
    } catch (error) {
      toast.push('error', 'That run was not queued.', messageOf(error));
    } finally {
      setQueueing(false);
    }
  }

  if (state.loading) return <LoadingState label="Loading" />;
  if (state.error !== null) return <ErrorBox message={state.error} />;

  const runs = state.data?.runs ?? [];
  const deltas = state.data?.openDeltas ?? [];

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Balance check</h1>
        <p className="page-head__sub">
          Your own records, checked against what your crypto provider and payment provider say.
        </p>
      </div>

      {canRun && (
        <Card title="Run one now">
          <Select value={scope} onChange={(e) => setScope(e.target.value as ReconciliationScope)}>
            {SCOPES.map((s) => (
              <option key={s} value={s}>
                {SCOPE_LABELS[s]}
              </option>
            ))}
          </Select>
          <p className="muted">{SCOPE_COPY[scope]}</p>
          <Button variant="primary" onClick={() => void run()} loading={queueing}>
            Run this check
          </Button>
          <p className="muted">
            This does not run instantly. It starts within a minute; reload the page to see the result.
          </p>
        </Card>
      )}

      <Card title={`Differences found${deltas.length > 0 ? ` (${deltas.length})` : ''}`}>
        {deltas.length === 0 ? (
          <EmptyState
            glyph="✓"
            title="Nothing disagrees"
            hint="Every balance matched closely enough to ignore."
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Kind</th>
                  <th>Asset</th>
                  <th className="num">Expected</th>
                  <th className="num">Actual</th>
                  <th className="num">Difference</th>
                  <th>Detail</th>
                  <th>Found</th>
                </tr>
              </thead>
              <tbody>
                {deltas.map((d) => (
                  <tr key={d.id}>
                    <td>{d.kind.replace(/_/g, ' ')}</td>
                    <td>{d.currency}</td>
                    {/*
                     * Printed as the raw minor-unit strings the run recorded, not formatted as
                     * money. A delta is a disagreement between two figures and the exact
                     * digits are the evidence; rounding them for display is how a one-unit
                     * discrepancy becomes invisible.
                     */}
                    <td className="num">{d.expected}</td>
                    <td className="num">{d.actual}</td>
                    <td className="num">
                      <strong>{d.delta}</strong>
                    </td>
                    <td>
                      {d.detail}
                      {d.externalId !== '' && (
                        <>
                          <br />
                          <span className="muted">{d.externalId}</span>
                        </>
                      )}
                    </td>
                    <td>{relativeTime(d.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Runs">
        {runs.length === 0 ? (
          <p className="muted">Nothing has been checked yet.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>What was checked</th>
                  <th>Status</th>
                  <th className="num">Checked</th>
                  <th className="num">Mismatches</th>
                  <th>Started</th>
                  <th>Finished</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id}>
                    <td>{SCOPE_LABELS[r.scope]}</td>
                    <td>
                      <Badge
                        tone={
                          r.status === 'clean'
                            ? 'green'
                            : r.status === 'failed'
                              ? 'red'
                              : r.status === 'deltas'
                                ? 'amber'
                                : 'blue'
                        }
                      >
                        {RUN_STATUS_LABELS[r.status]}
                      </Badge>
                      {/* A run that paused conversions is the loudest thing this table can
                          say, so it is not left to the note column. */}
                      {r.paused && <Badge tone="red">stopped trading</Badge>}
                    </td>
                    <td className="num">{r.checked}</td>
                    <td className="num">{r.deltas}</td>
                    <td>{formatDateTime(r.startedAt)}</td>
                    <td>
                      {r.finishedAt === null ? (
                        <span className="muted">running</span>
                      ) : (
                        relativeTime(r.finishedAt)
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
